export interface TurnstileApi {
  render: (el: HTMLElement, o: { sitekey: string; callback: (t: string) => void; 'expired-callback': () => void; 'error-callback': () => void; appearance?: string }) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;

/** Loads Cloudflare's script once, on demand (the form calls this when the email field is focused). */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SRC;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : ((loading = null), s.remove(), reject(new Error('turnstile missing'))));
    s.onerror = () => {
      loading = null;
      s.remove();
      reject(new Error('turnstile failed to load'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/**
 * Renders the widget into `el` and returns its id. `onExpire` runs when the token expires; `onError` when the widget gives up (a blocker, a wrong
 * hostname, no network). Without `onError`, an error also runs `onExpire`, so a stale token is never sent.
 */
export function renderTurnstile(el: HTMLElement, siteKey: string, onToken: (t: string) => void, onExpire: () => void, onError?: () => void): string {
  if (!window.turnstile) throw new Error('turnstile not loaded');
  return window.turnstile.render(el, { sitekey: siteKey, callback: onToken, 'expired-callback': onExpire, 'error-callback': onError ?? onExpire, appearance: 'interaction-only' });
}

/** Asks for a fresh token (a token is good for one submit). Silent when the widget is gone. */
export function resetTurnstile(id?: string): void {
  try {
    window.turnstile?.reset(id);
  } catch {
    /* the widget was removed */
  }
}

/** Takes the widget out of the page (after a success, and when the form goes away). Silent when it is already gone. */
export function removeTurnstile(id?: string): void {
  try {
    if (id !== undefined) window.turnstile?.remove(id);
  } catch {
    /* the widget was removed */
  }
}
